import { Edge, Node } from "reactflow";
import { CForm } from "../../types";
import CControl from "../../forms/types/CControl";
import { FlowNodeColors } from "../components/FlowNodeConstants";
import { IFlowNodeData } from "../components/IFlowNodeData";
import CComboBox from "../../forms/types/CComboBox";

abstract class FlowNode<T> {

    $engine: FlowEngine;
    $schema: Node<IFlowNodeData>;

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        this.$engine = engine;
        this.$schema = schema;
    }

    get type(): string {
        return this.$schema.type;
    }

    get id(): string {
        return this.$schema.id;
    }

    getInputNode<T>(targetHandleId: string): FlowNode<T> {
        const edge = this.$engine.findInputEdges(x => x.$schema.target === this.$schema.id && x.$schema.targetHandle === targetHandleId);
        return edge?.getSourceNode<T>();
    }

    public abstract execute(deep: number): T;

    protected log(message: string, deep: number, style: string) {
        console.log(`%c${'\t'.repeat(deep)}${message}`, `${style}; padding: 5px;`);
    }

}

class FEdge {

    $engine: FlowEngine;
    $schema: Edge;

    constructor(engine: FlowEngine, schema: Edge) {
        this.$engine = engine;
        this.$schema = schema;
    }

    get id(): string {
        return this.$schema.id;
    }

    get source(): string {
        return this.$schema.source;
    }

    get sourceHandle(): string {
        return this.$schema.sourceHandle;
    }

    get target(): string {
        return this.$schema.target;
    }

    get targetHandle(): string {
        return this.$schema.targetHandle;
    }

    getSourceNode<TResult>(): FlowNode<TResult> {
        const source = this.$engine.nodes.find(x => x.id == this.$schema.source);
        return source as any;
    }

    getTarget<TResult>(): FlowNode<TResult> {
        const source = this.$engine.nodes.find(x => x.id == this.$schema.target);
        return source as any;
    }

}

class SumNode extends FlowNode<number> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): number {
        const num1 = this.getInputNode<number>('num1')?.execute(deep + 1) ?? 0;
        const num2 = this.getInputNode<number>('num2')?.execute(deep + 1) ?? 0;
        const result = num1 + num2;
        return result;
    }

}

class AndNode extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const result = input1 && input2;
        return result;
    }

}

class And3Node extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const input3 = this.getInputNode<boolean>('input3')?.execute(deep + 1) ?? false;
        const result = input1 && input2 && input3;
        return result;
    }

}

class And4Node extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const input3 = this.getInputNode<boolean>('input3')?.execute(deep + 1) ?? false;
        const input4 = this.getInputNode<boolean>('input4')?.execute(deep + 1) ?? false;
        const result = input1 && input2 && input3 && input4;
        return result;
    }

}

class And6Node extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const input3 = this.getInputNode<boolean>('input3')?.execute(deep + 1) ?? false;
        const input4 = this.getInputNode<boolean>('input4')?.execute(deep + 1) ?? false;
        const input5 = this.getInputNode<boolean>('input5')?.execute(deep + 1) ?? false;
        const input6 = this.getInputNode<boolean>('input6')?.execute(deep + 1) ?? false;
        const result = input1 && input2 && input3 && input4 && input5 && input6;
        return result;
    }

}

class OrNode extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const result = input1 || input2 || false;
        return result;
    }

}

class Or3Node extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const input3 = this.getInputNode<boolean>('input3')?.execute(deep + 1) ?? false;
        const result = input1 || input2 || input3 || false;
        return result;
    }

}

class Or4Node extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const input3 = this.getInputNode<boolean>('input3')?.execute(deep + 1) ?? false;
        const input4 = this.getInputNode<boolean>('input4')?.execute(deep + 1) ?? false;
        const result = input1 || input2 || input3 || input4 || false;
        return result;
    }

}

class Or6Node extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode<boolean>('input1')?.execute(deep + 1) ?? false;
        const input2 = this.getInputNode<boolean>('input2')?.execute(deep + 1) ?? false;
        const input3 = this.getInputNode<boolean>('input3')?.execute(deep + 1) ?? false;
        const input4 = this.getInputNode<boolean>('input4')?.execute(deep + 1) ?? false;
        const input5 = this.getInputNode<boolean>('input5')?.execute(deep + 1) ?? false;
        const input6 = this.getInputNode<boolean>('input6')?.execute(deep + 1) ?? false;
        const result = input1 || input2 || input3 || input4 || input5 || input6 || false;
        return result;
    }

}

class EqualsNode extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode('input1')?.execute(deep + 1) ?? this.$schema.data.inputValue1;
        const input2 = this.getInputNode('input2')?.execute(deep + 1) ?? this.$schema.data.inputValue2;
        const result = input1 == input2;
        return result;
    }


}

class NotEqualsNode extends FlowNode<boolean> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): boolean {
        const input1 = this.getInputNode('input1')?.execute(deep + 1) ?? this.$schema.data.inputValue1;
        const input2 = this.getInputNode('input2')?.execute(deep + 1) ?? this.$schema.data.inputValue2;
        const result = input1 != input2;
        return result;
    }

}

class FieldSelectorNode extends FlowNode<any> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): any {
        const field = this.$engine.form.fields.firstOrDefault(x => x.id == this.$schema.data.fieldId);
        return field?.value;
    }

}

class ComboBoxNode extends FlowNode<any> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number): any {

        // Obtener campo
        const field = this.$engine.form.fields.cast<CComboBox>().firstOrDefault(x => x.id == this.$schema.data.fieldId);
        if (field == null)
            return null;
        debugger
        const value = field.selectedText;
        this.log(`[ComboBoxNode('${field.name}')] = ${value}`, deep, `color: white; background-color: ${FlowNodeColors.fieldSelector}`);

        return value;

    }

}

class ResultNode extends FlowNode<any> {

    constructor(engine: FlowEngine, schema: Node<IFlowNodeData>) {
        super(engine, schema);
    }

    execute(deep: number = 0): any {
        const node = this.getInputNode('input');
        const result = node.execute(1);
        this.log(`[Result] = ${result}`, 0, `color: white; background-color: ${FlowNodeColors.result}`);
        return result;
    }


}

const types = {
    aggregate: SumNode,
    and: AndNode,
    and3: And3Node,
    and4: And4Node,
    and6: And6Node,
    boolean: SumNode,
    countItems: SumNode,
    dataTableColumnSelector: SumNode,
    entityAttribute: SumNode,
    equals: EqualsNode,
    notEquals: NotEqualsNode,
    fieldSelector: FieldSelectorNode,
    comboboxField: ComboBoxNode,
    filterItems: SumNode,
    number: SumNode,
    or: OrNode,
    or3: Or3Node,
    or4: Or4Node,
    or6: Or6Node,
    percentage: SumNode,
    required: SumNode,
    result: ResultNode,
    sum: SumNode,
    textUpdater: SumNode,
}

class FlowEngine {

    $nodes: FlowNode<IFlowNodeData>[] = [];
    $edges: FEdge[] = [];
    $form: CForm;

    constructor(nodes: Node<IFlowNodeData>[], edges: Edge[], form: CForm) {
        this.$nodes = nodes.map(x => types[ x.type ] != null ? new types[ x.type ](this, x) : null).filter(x => x != null);
        this.$edges = edges.map(x => new FEdge(this, x));
        this.$form = form;
    }

    get nodes(): FlowNode<IFlowNodeData>[] {
        return this.$nodes;
    }

    get edges(): FEdge[] {
        return this.$edges;
    }

    get form(): CForm {
        return this.$form;
    }

    execute(): any {

        // Get node result
        const resultNode = this.$nodes.find(x => x.type === 'result')
        if (resultNode == null)
            return null;

        // Execute node
        const result = resultNode.execute(1);
        return result;

    }

    findInputEdges(predicate: (edge: FEdge) => boolean): FEdge {
        return this.$edges.find(predicate);
    }


}

export const createRuleEngine = function executeRule<T>(fieldNane: string, nodes: Node<IFlowNodeData>[], edges: Edge[], form: CForm): () => T {
    const engine = new FlowEngine(nodes, edges, form);
    return () => {
        //console.groupCollapsed(`Rule engine for field ${fieldNane}`)
        const result = engine.execute();
        //console.groupEnd();
        return result;
    }
}